'use client'
import {ReactNode, useEffect} from "react";
import {ChatContext} from "@web-libs/features/chat/application/use-chat.context";
import {useCreatePaginatedChatConcern} from "@web-libs/features/chat/infrastructure/use-paginated-chat.concern";
import {useInitOnce} from "@web-libs/util/hooks/use-init-once";
import {
    provideTrmzFileDtoConverter
} from "@web-libs/features/files/trmz-file/infrastructure/provide-trmz-file-dto-converter";
import {useHttpContext} from "@web-libs/core/http/infrastructure/client-side/http.context";
import {useParams} from "next/navigation";
import {createTrmzFileRepository} from "@web-libs/features/files/trmz-file/api/trmz-file.repository";
import {useAuth} from "@web-libs/features/auth/main/application/use-auth.concern";
import {ChatSocket} from "@web-libs/features/chat/api/chat.socket";
import {createRemotePaginatedChatRepository} from "@web-libs/features/chat/api/remote.repository";
import {ChatRoomChangeCallback} from "@web-libs/features/chat/application/use-chat.interface";

export const ChatProvider = ({children} : {children: ReactNode}) => {

    const httpClient = useHttpContext()
    const urlParams = useParams()
    const tenantId = urlParams.tenant as string

    const authConcern = useAuth()
    const chatSocket: ChatSocket = useInitOnce(() => {
        const authToken = authConcern.getAuthToken()
        return new ChatSocket(tenantId, authToken ?? "", () => {
            authConcern.refreshToken()
        });
    })


    const trmzFileConverter = provideTrmzFileDtoConverter(httpClient.baseUrl, tenantId)
    const trmzFileRepo = createTrmzFileRepository(httpClient, tenantId)
    const chatRepo = useInitOnce(() => {
        return createRemotePaginatedChatRepository(httpClient, trmzFileRepo, trmzFileConverter, chatSocket)
    })

    const chatConcern = useCreatePaginatedChatConcern(chatRepo)

    useEffect(() => {
        const handleChatroomsChange:ChatRoomChangeCallback = (chatrooms) => {
            chatSocket.setActiveIds(chatrooms.map((chatroom) => chatroom.id))
        }

        const handleSocketConnectionChange = (isConnected: boolean) => {
            chatConcern.connectionState.setIsConnected(isConnected)
        }

        const handleTokenChange = (token:string|null) => {
            if(token){
                chatSocket.setAccessToken(token)
            }
        }

        authConcern.registerOnTokenChange(handleTokenChange)
        chatConcern.onChatroomsChange(handleChatroomsChange);
        chatSocket.onConnectionChange(handleSocketConnectionChange)

        return () => {
            chatConcern.removeChatroomChangeListener(handleChatroomsChange)
            chatSocket.removeOnConnectionChangeCallback()
            authConcern.removeOnTokenChange(handleTokenChange)
        }
    },[])

    return (
        <ChatContext.Provider value={chatConcern}>
            {children}
        </ChatContext.Provider>
    )
}
